import React, { useMemo } from 'react';
import { useFarmerFormValidation } from '@applicationKYC/LegalEntityQuestionnaire/validation/useFarmerFormValidation';
import * as Yup from 'yup';
import { Trans, useTranslation } from 'react-i18next';
import breakpointSizes from '@applicationKYC/LegalEntityQuestionnaire/styled-theme-styles/global-style-variables.style';
import useWindowDimensions from '@applicationKYC/LegalEntityQuestionnaire/Utils/window-size.service';
import { Field, Input, Loader, TextArea } from '@single-platform/components';
import {
  FarmersQuestionnaireHeader,
  PageSecondaryTextStyles,
  PageSubtitle
} from '@applicationKYC/FarmersQuestionnaire/farmersQuestionnaire.style';
import {
  FormStyles,
  PageHeader
} from '@applicationKYC/LegalEntityQuestionnaire/screens/renderPage/renderPage.style';
import { ButtonSection } from '@applicationKYC/LegalEntityQuestionnaire/components';
import { useSelector } from 'react-redux';
import {
  ButtonSectionWrapperStyle,
  EmphasizedBrandColorText
} from '@applicationKYC/FarmersQuestionnaire/questionnaireSections/formStyles';
import { RadioChipOption } from '@applicationKYC/FarmersQuestionnaire/components';
import { yesNoChipOptions } from '@applicationKYC/FarmersQuestionnaire/constants';
import { selectKycAppState } from '@redux';

const defaultValues = {
  doesFarmUseCash: null,
  proportionOfPaymentsInCash: null,
  maximumSingleCashAmount: null,
  howCashIsUsed: null
};

const cashAmountOptions = [
  { label: 'choices_proportion_of_payments_cash_less_than_10_percent', value: 'LessThan10Percent' },
  {
    label: 'choices_proportion_of_payments_cash_from_11_to_20_percent',
    value: 'From11PercentTo20Percent'
  },
  {
    label: 'choices_proportion_of_payments_cash_from_21_to_30_percent',
    value: 'From21PercentTo30Percent'
  },
  {
    label: 'choices_proportion_of_payments_cash_more_than_31_percent',
    value: 'MoreThan31Percent'
  }
];

export const FarmCashInformationPage = ({ onSubmit, isAtBottom }) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const { loading, farmerInformation } = useSelector((state) => state.FarmersQuestionnaireReducer);
  const isBank = process.env.REACT_APP_TRADEMARK.includes('Bank');
  const applicationState = useSelector(selectKycAppState);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        doesFarmUseCash: Yup.bool().required(t('validation_must_be_one_of')).nullable(),
        proportionOfPaymentsInCash: Yup.string().when('doesFarmUseCash', {
          is: true,
          then: Yup.string().required(t('validation_must_be_one_of')).nullable(),
          otherwise: Yup.string().notRequired().nullable()
        }),
        maximumSingleCashAmount: Yup.string().when('doesFarmUseCash', {
          is: true,
          then: Yup.string()
            .required(t('validation_required_field'))
            .nullable()
            .test('isPositive', 'validation_must_be_positiive', (value) => parseInt(value) > 0),
          otherwise: Yup.string().notRequired().nullable()
        }),
        howCashIsUsed: Yup.string().when('doesFarmUseCash', {
          is: true,
          then: Yup.string().required(t('validation_required_field')).nullable(),
          otherwise: Yup.string().notRequired().nullable()
        })
      }),
    [t]
  );

  const existingValues =
    farmerInformation?.farmPaymentsAndTransactionsSection?.farmCashInformationPage;
  const initialValues = { ...defaultValues, ...existingValues };

  const { validationHelpers, getValues, setTouchedOnAll, handleSubmit } = useFarmerFormValidation({
    initialValues,
    validationSchema
  });

  const onHandleSubmit = async (e) => {
    e.preventDefault();
    await handleSubmit(
      async (data) => {
        if (data.doesFarmUseCash === false) {
          await onSubmit?.({ doesFarmUseCash: false }, 'farmCashInformationPage');
        } else {
          await onSubmit?.(data, 'farmCashInformationPage');
        }
      },
      () => setTouchedOnAll()
    )();
  };

  if (loading) return <Loader />;

  const values = getValues();

  return (
    <FormStyles small={width < breakpointSizes.phone}>
      <FarmersQuestionnaireHeader noSubHeader>
        <PageHeader>
          <Trans
            t={t}
            components={{ coloredText: <EmphasizedBrandColorText isBank={isBank} /> }}
            i18nKey={'farmers_cash_header'}
          />
        </PageHeader>
        <PageSubtitle>{t('farmers_cash_subtitle')}</PageSubtitle>
      </FarmersQuestionnaireHeader>

      <Field
        Component={RadioChipOption}
        options={yesNoChipOptions}
        value={values.doesFarmUseCash}
        name="doesFarmUseCash"
        validateOnChange
        validationHelpers={validationHelpers}
        isInModal
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />

      {values.doesFarmUseCash && (
        <>
          <PageSecondaryTextStyles>{t('farmers_cash_percentage')}</PageSecondaryTextStyles>

          <Field
            Component={RadioChipOption}
            options={cashAmountOptions}
            value={values.proportionOfPaymentsInCash}
            name="proportionOfPaymentsInCash"
            validateOnChange
            validationHelpers={validationHelpers}
            isInModal
            disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
          />

          <PageSecondaryTextStyles>{t('farmers_cash_max_amount')}</PageSecondaryTextStyles>

          <Field
            Component={Input}
            type="number"
            name="maximumSingleCashAmount"
            value={values.maximumSingleCashAmount}
            placeholder={t('farmers_max_amount_placeholder')}
            validationHelpers={validationHelpers}
            required
            validateOnChange
            disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
          />

          <div>
            <PageSecondaryTextStyles>{t('farmers_how_cash_is_used')}</PageSecondaryTextStyles>
            <PageSubtitle>{t('farmers_how_cash_is_used_description')}</PageSubtitle>
          </div>

          <Field
            Component={TextArea}
            name="howCashIsUsed"
            value={values.howCashIsUsed}
            placeholder={t('question_enter_description')}
            validationHelpers={validationHelpers}
            required
            validateOnChange
            autoHeight={true}
            maxHeight={288}
            validateOnBlur
            maxLength={500}
            counter={true}
            disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
          />
        </>
      )}

      <ButtonSectionWrapperStyle isAtBottom={isAtBottom}>
        <ButtonSection
          secondButtonClick={onHandleSubmit}
          secondButtonPrimary
          secondButtonText={
            applicationState?.state === 'UserFillingApplication'
              ? t('button_save_and_continue')
              : t('button_continue')
          }
        />
      </ButtonSectionWrapperStyle>
    </FormStyles>
  );
};
